import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const DOMAIN_BASE = "cc.somebugs.com";

/**
 * Validates that a redirect URL points to a trusted *.cc.somebugs.com subdomain.
 * Only HTTPS URLs on subdomains of the domain base are considered valid.
 */
function isValidRedirect(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return false;

    const hostname = parsed.hostname;
    // Must be the domain base itself or a subdomain of it
    return hostname === DOMAIN_BASE || hostname.endsWith(`.${DOMAIN_BASE}`);
  } catch {
    return false;
  }
}

/** Check whether a URL points to a *.cc.somebugs.com subdomain different from the current origin. */
function isCrossSubdomainRedirect(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.endsWith(`.${DOMAIN_BASE}`)) return false;
    return parsed.origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * /login route handler for cross-subdomain auth redirects.
 *
 * This component is rendered INSIDE the KeycloakAuthGate, which means:
 * - If the user is NOT authenticated, the gate will auto-redirect to Keycloak
 *   (passing the ?redirect= parameter through via the state param).
 * - If the user IS authenticated, this component renders and handles the redirect.
 *
 * Flow:
 * 1. Instance at test1.cc.somebugs.com redirects unauthenticated user to:
 *    cc.somebugs.com/login?redirect=https://test1.cc.somebugs.com/
 * 2. KeycloakAuthGate either:
 *    a. Initiates Keycloak login (if not authenticated) — redirect param is preserved
 *    b. Passes through to this component (if already authenticated)
 * 3. This component redirects to the instance's /auth/sso endpoint with the access token
 */
export function LoginRedirect() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const redirect = searchParams.get("redirect");

    if (redirect && isValidRedirect(redirect)) {
      if (isCrossSubdomainRedirect(redirect)) {
        // Pass the access token to the instance's SSO endpoint
        const accessToken = localStorage.getItem("cloud_access_token");
        if (accessToken) {
          const ssoUrl = new URL("/api/auth/keycloak/sso", redirect);
          ssoUrl.searchParams.set("access_token", accessToken);
          window.location.href = ssoUrl.toString();
          return;
        }
      }
      // Same-origin redirect or no token — navigate directly
      window.location.href = redirect;
      return;
    }

    // No valid redirect — go to homepage
    navigate("/", { replace: true });
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-bg">
      <div className="text-center">
        <div className="w-6 h-6 mx-auto mb-3 border-2 border-warm-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-warm-500 text-sm">{t('login.redirecting')}</p>
      </div>
    </div>
  );
}
